import { Directive, ElementRef, Input, OnInit, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';
import { formatPhoneNumber } from '../helpers/phone';

/**
 * Formats an ngModel-bound input as a US phone number: (123)-456-7890.
 *
 * Usage: <input [phoneFormat]="true" [(ngModel)]="model.phone">
 * Passing a falsy value (e.g. for non-phone fields in a generic loop) makes it
 * a no-op, so it can be bound conditionally on every input in a *ngFor.
 */
@Directive({
  selector: '[phoneFormat]',
  standalone: true,
})
export class PhoneFormatDirective implements OnInit {
  @Input() phoneFormat: boolean | string = true;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    @Optional() private ngControl: NgControl,
  ) {}

  private get enabled(): boolean {
    return this.phoneFormat === '' || this.phoneFormat === true || this.phoneFormat === 'true';
  }

  ngOnInit(): void {
    if (!this.enabled) return;
    const control = this.ngControl?.control;
    if (!control) return;

    // Reformat whenever the value changes — covers both typing and values that
    // arrive asynchronously (e.g. after the record is fetched).
    control.valueChanges?.subscribe((value) => this.apply(value));

    // Format whatever is already there on init.
    this.apply(control.value);
  }

  private apply(value: unknown): void {
    const control = this.ngControl?.control;
    if (!control) return;
    const formatted = formatPhoneNumber(value as string);
    if ((value ?? '') !== formatted) {
      control.setValue(formatted, { emitEvent: false });
    }
    if (this.el.nativeElement.value !== formatted) {
      this.el.nativeElement.value = formatted;
    }
  }
}
